import { WhiteLabelStyles } from 'signer-app/white-labeling/types';
import { themes } from 'signer-app/white-labeling/themes';
import StringHelper from 'js/sign-components/common/string-helpers';
import { whiteLabelingOld } from 'js/sign-components/common/white-labeling-old';

export type ColorConfig = Record<string, string | boolean>;

const COLOR_KEYS: ReadonlySet<string> = new Set(
  Object.keys(themes.vis2023) as (keyof WhiteLabelStyles)[],
);

export interface AppliedPreset {
  styles: WhiteLabelStyles;
  unrecognised: string[];
  sparse: string[];
}

export type ParseUploadedColorPresetResult =
  | { ok: true; config: ColorConfig }
  | { ok: false; error: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normaliseConfigEntry(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  return undefined;
}

function isRecognisedWhiteLabelKey(rawKey: string): boolean {
  const camelKey = StringHelper.toCamelCase(rawKey);
  return COLOR_KEYS.has(camelKey) || COLOR_KEYS.has(rawKey);
}

/**
 * Accepts a flat JSON object of API-style keys (snake_case or camelCase) to
 * string values, e.g. `{ "page_background_color": "#f0f6f9", ... }`.
 */
export function parseUploadedColorPresetJson(
  parsed: unknown,
): ParseUploadedColorPresetResult {
  if (!isPlainObject(parsed)) {
    return { ok: false, error: 'JSON must be an object.' };
  }

  const config: ColorConfig = {};
  for (const [key, value] of Object.entries(parsed)) {
    const v = normaliseConfigEntry(value);
    if (v !== undefined) {
      config[key] = v;
    } else {
      return {
        ok: false,
        error:
          'JSON must be a single-level object: each property must be a string (no nested objects or arrays).',
      };
    }
  }

  if (Object.keys(config).length === 0) {
    return {
      ok: false,
      error: 'No color properties found. Expected a flat object of color keys.',
    };
  }

  if (!Object.keys(config).some(isRecognisedWhiteLabelKey)) {
    return {
      ok: false,
      error:
        'No recognised white-label properties. Use API field names (e.g. page_background_color, primary_button_color).',
    };
  }

  return {
    ok: true,
    config,
  };
}

export function exportPreset(preset: typeof whiteLabelingOld) {
  const fileName = 'colors.json';
  const data = new Blob([JSON.stringify(preset)], {
    type: 'text/json',
  });
  const jsonURL = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  document.body.appendChild(link);
  link.href = jsonURL;
  link.setAttribute('download', fileName);
  link.click();
  document.body.removeChild(link);
}

export function validateKeys(preset: ColorConfig): {
  unrecognised: string[];
  known: Partial<WhiteLabelStyles>;
} {
  const unrecognised: string[] = [];
  const known: Partial<WhiteLabelStyles> = {};

  for (const [rawKey, value] of Object.entries(preset)) {
    const camelKey = StringHelper.toCamelCase(rawKey);
    if (COLOR_KEYS.has(camelKey)) {
      (known as Record<string, string | boolean>)[camelKey] = value;
    } else if (COLOR_KEYS.has(rawKey)) {
      (known as Record<string, string | boolean>)[rawKey] = value;
    } else {
      unrecognised.push(rawKey);
    }
  }

  return { unrecognised, known };
}

/**
 * Validates a preset config against the canonical WhiteLabelStyles schema,
 * merges known values over the default theme, and reports unrecognised keys
 * and properties that fell back to defaults.
 *
 * Incoming keys are normalised from snake_case to camelCase using the same
 * StringHelper.toCamelCase that the rest of the codebase uses (mirrors the
 * PHP lcfirst(ucwords(...)) conversion in signer/actions.class.php).
 * Unrecognised keys are reported using their *original* key name.
 */
export function applyPreset(
  preset: ColorConfig,
  defaults: WhiteLabelStyles = themes.vis2023,
): AppliedPreset {
  const { unrecognised, known } = validateKeys(preset);
  const sparse = [...COLOR_KEYS].filter((k) => !(k in known));

  return {
    styles: { ...defaults, ...known },
    unrecognised,
    sparse,
  };
}
